import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Eye, MapPin } from 'lucide-react';
interface Notification {
  id: number;
  type: 'purchase' | 'viewing';
  name: string;
  location: string;
  product?: string;
  time: string;
}
const notifications: Omit<Notification, 'id'>[] = [
// USA
{
  type: 'purchase',
  name: 'Michael R.',
  location: 'Denver, CO, USA',
  product: '3x Pack',
  time: '2 minutes ago'
}, {
  type: 'viewing',
  name: 'Sarah M.',
  location: 'Austin, TX, USA',
  time: 'Just now'
}, {
  type: 'purchase',
  name: 'Jennifer L.',
  location: 'Seattle, WA, USA',
  product: '1x Pack',
  time: '5 minutes ago'
}, {
  type: 'viewing',
  name: 'David T.',
  location: 'Miami, FL, USA',
  time: 'Just now'
}, {
  type: 'purchase',
  name: 'Lisa W.',
  location: 'Portland, OR, USA',
  product: '5x Pack',
  time: '3 minutes ago'
},
// Canada
{
  type: 'purchase',
  name: 'James C.',
  location: 'Toronto, ON, Canada',
  product: '3x Pack',
  time: '4 minutes ago'
}, {
  type: 'viewing',
  name: 'Emma T.',
  location: 'Vancouver, BC, Canada',
  time: 'Just now'
}, {
  type: 'purchase',
  name: 'Robert M.',
  location: 'Montreal, QC, Canada',
  product: '1x Pack',
  time: '6 minutes ago'
},
// UK
{
  type: 'purchase',
  name: 'Oliver S.',
  location: 'London, UK',
  product: '3x Pack',
  time: '3 minutes ago'
}, {
  type: 'viewing',
  name: 'Sophie W.',
  location: 'Manchester, UK',
  time: 'Just now'
}, {
  type: 'purchase',
  name: 'George H.',
  location: 'Birmingham, UK',
  product: '5x Pack',
  time: '7 minutes ago'
},
// Australia
{
  type: 'purchase',
  name: 'Liam P.',
  location: 'Sydney, NSW, Australia',
  product: '3x Pack',
  time: '5 minutes ago'
}, {
  type: 'viewing',
  name: 'Olivia K.',
  location: 'Melbourne, VIC, Australia',
  time: 'Just now'
}, {
  type: 'purchase',
  name: 'Noah B.',
  location: 'Brisbane, QLD, Australia',
  product: '1x Pack',
  time: '4 minutes ago'
},
// New Zealand
{
  type: 'purchase',
  name: 'Jack M.',
  location: 'Auckland, New Zealand',
  product: '3x Pack',
  time: '6 minutes ago'
}, {
  type: 'viewing',
  name: 'Amelia R.',
  location: 'Wellington, New Zealand',
  time: 'Just now'
}, {
  type: 'purchase',
  name: 'Charlotte D.',
  location: 'Christchurch, New Zealand',
  product: '5x Pack',
  time: '8 minutes ago'
}];
export function SocialProofNotifications() {
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const [notificationIndex, setNotificationIndex] = useState(0);
  useEffect(() => {
    // Show first notification after 3 seconds
    const initialTimeout = setTimeout(() => {
      setCurrentNotification({
        ...notifications[0],
        id: 0
      });
    }, 3000);
    return () => clearTimeout(initialTimeout);
  }, []);
  useEffect(() => {
    if (currentNotification === null) return;
    // Hide notification after 5 seconds
    const hideTimeout = setTimeout(() => {
      setCurrentNotification(null);
    }, 5000);
    // Show next notification after 8 seconds (3 seconds gap)
    const showTimeout = setTimeout(() => {
      const nextIndex = (notificationIndex + 1) % notifications.length;
      setNotificationIndex(nextIndex);
      setCurrentNotification({
        ...notifications[nextIndex],
        id: nextIndex
      });
    }, 8000);
    return () => {
      clearTimeout(hideTimeout);
      clearTimeout(showTimeout);
    };
  }, [currentNotification, notificationIndex]);
  return <AnimatePresence data-id="element-531">
      {currentNotification && <motion.div initial={{
      opacity: 0,
      y: 50,
      scale: 0.9
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.9
    }} className="fixed bottom-6 left-6 z-50 max-w-sm" data-id="element-532">
          <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4 flex items-start gap-3" data-id="element-533">
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${currentNotification.type === 'purchase' ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-534">
              {currentNotification.type === 'purchase' ? <ShoppingCart className="w-5 h-5 text-green-600" data-id="element-535" /> : <Eye className="w-5 h-5 text-blue-600" data-id="element-536" />}
            </div>
            <div className="flex-1 min-w-0" data-id="element-537">
              <p className="text-sm font-semibold text-gray-900" data-id="element-538">
                {currentNotification.type === 'purchase' ? `${currentNotification.name} just purchased` : `${currentNotification.name} is viewing this page`}
              </p>
              {currentNotification.product && <p className="text-sm text-gray-600 font-medium" data-id="element-539">
                  {currentNotification.product}
                </p>}
              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500" data-id="element-540">
                <MapPin className="w-3 h-3" data-id="element-541" />
                <span data-id="element-542">{currentNotification.location}</span>
                <span data-id="element-543">•</span>
                <span data-id="element-544">{currentNotification.time}</span>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}